import * as React from "react";
import { addHours, format, setHours, setMinutes } from "date-fns";
import { CalendarIcon, X, ChevronUp, ChevronDown, Info } from "lucide-react";
import { cn } from "@/lib/utils";
import { Calendar } from "@/components/ui/calendar";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";

interface FloatingDateTimePickerProps {
	value: string;
	onChange: (val: string) => void;
	label: string;
	error?: string;
	disabled?: boolean;
	tabIndex?: number;
}

const TimePicker = ({
	dateValue,
	handleTimeChange,
	handleDateRollover,
	minDateTime,
}: {
	dateValue: Date | undefined;
	handleTimeChange: (type: "hours" | "minutes", val: number) => void;
	minDateTime: Date;
	handleDateRollover: (newDate: Date) => void;
}) => {
	const hours = dateValue ? dateValue.getHours() : minDateTime.getHours();
	const minutes = dateValue ? dateValue.getMinutes() : minDateTime.getMinutes();

	const [localHour, setLocalHour] = React.useState(
		hours.toString().padStart(2, "0")
	);
	const [localMin, setLocalMin] = React.useState(
		minutes.toString().padStart(2, "0")
	);

	React.useEffect(() => {
		const base = dateValue ?? minDateTime;
		setLocalHour(base.getHours().toString().padStart(2, "0"));
		setLocalMin(base.getMinutes().toString().padStart(2, "0"));
	}, [dateValue]);

	// ── Min constraints ────────────────────────────────────────────────────────
	const isMinDay = dateValue
		? dateValue.toDateString() === minDateTime.toDateString()
		: true;
	const isToday = dateValue
		? dateValue.toDateString() === new Date().toDateString()
		: true;

	const minHour24 = isMinDay ? minDateTime.getHours() : 0;
	const minMin = isMinDay && hours === minHour24 ? minDateTime.getMinutes() : 0;

	const isDecrementHourDisabled =
		(isMinDay && hours <= minHour24) || (isToday && hours <= 0);
	const isDecrementMinDisabled =
		isMinDay && hours === minHour24 && minutes <= minMin;

	// ── Input change ───────────────────────────────────────────────────────────
	const handleInputChange = (type: "hours" | "minutes", val: string) => {
		if (val === "") {
			type === "hours" ? setLocalHour("") : setLocalMin("");
			return;
		}
		if (!/^\d+$/.test(val)) return;

		if (type === "hours") {
			const num = parseInt(val, 10);
			if (num > 23) return; // ✅ max 23 for 24hr
			setLocalHour(val);
			if (num >= 0 && num <= 23) handleTimeChange("hours", num);
		} else {
			const num = parseInt(val, 10);
			if (num > 59) return;
			setLocalMin(val);
			if (num >= 0 && num <= 59) handleTimeChange("minutes", num);
		}
	};

	// ── Blur clamp ─────────────────────────────────────────────────────────────
	const handleBlur = (type: "hours" | "minutes") => {
		if (type === "hours") {
			let num = parseInt(localHour, 10);
			if (isNaN(num) || num < 0) num = 0;
			if (num > 23) num = 23;
			if (isMinDay && num < minHour24) num = minHour24; // ✅ clamp to min
			handleTimeChange("hours", num);
			setLocalHour(num.toString().padStart(2, "0"));
		} else {
			let num = parseInt(localMin, 10);
			if (isNaN(num) || num < 0) num = 0;
			if (num > 59) num = 59;
			if (isMinDay && hours === minHour24 && num < minMin) num = minMin; // ✅ clamp to min
			handleTimeChange("minutes", num);
			setLocalMin(num.toString().padStart(2, "0"));
		}
	};

	// ── Increment / Decrement ──────────────────────────────────────────────────
	const increment = (type: "hours" | "minutes") => {
		if (type === "hours") {
			const current = parseInt(localHour, 10);
			if (current >= 23) {
				// ✅ Roll over to next day at 00
				const nextDay = new Date(dateValue ?? minDateTime);
				nextDay.setDate(nextDay.getDate() + 1);
				nextDay.setHours(0, nextDay.getMinutes(), 0, 0);
				handleDateRollover(nextDay); // pass new date up
			} else {
				const next = current + 1;
				handleTimeChange("hours", next);
				setLocalHour(next.toString().padStart(2, "0"));
			}
		} else {
			const current = parseInt(localMin, 10);
			if (current >= 59) {
				const newDate = new Date(dateValue ?? minDateTime);
				const currentHour = parseInt(localHour, 10);
				if (currentHour >= 23) {
					newDate.setDate(newDate.getDate() + 1);
					newDate.setHours(0, 0, 0, 0); // next day 00:00
				} else {
					newDate.setHours(currentHour + 1, 0, 0, 0); // bump hour, reset mins
				}
				handleDateRollover(newDate); // ← single atomic update
			} else {
				const next = current + 1;
				handleTimeChange("minutes", next);
				setLocalMin(next.toString().padStart(2, "0"));
			}
		}
	};

	const decrement = (type: "hours" | "minutes") => {
		if (type === "hours") {
			if (isDecrementHourDisabled) return;
			const current = parseInt(localHour, 10);
			if (current <= 0) {
				//  Roll back to previous day at 23
				const prevDay = new Date(dateValue ?? minDateTime);
				prevDay.setDate(prevDay.getDate() - 1);
				prevDay.setHours(23, prevDay.getMinutes(), 0, 0);

				//  Don't go before today's date
				const today = new Date();
				today.setHours(0, 0, 0, 0);
				const prevDayDate = new Date(prevDay);
				prevDayDate.setHours(0, 0, 0, 0);

				if (prevDayDate < today) return; // block going before today

				handleDateRollover(prevDay);
			} else {
				const next = current - 1;
				//  Also block if going below minDateTime on min day
				if (isMinDay && next < minHour24) return;
				handleTimeChange("hours", next);
				setLocalHour(next.toString().padStart(2, "0"));
			}
		} else {
			if (isDecrementMinDisabled) return;
			const current = parseInt(localMin, 10);
			if (current <= 0) {
				const newDate = new Date(dateValue ?? minDateTime);
				const currentHour = parseInt(localHour, 10);
				if (currentHour <= 0) {
					// roll back to previous day 23:59
					newDate.setDate(newDate.getDate() - 1);
					newDate.setHours(23, 59, 0, 0);
					// block going before today
					const today = new Date();
					today.setHours(0, 0, 0, 0);
					const prevDay = new Date(newDate);
					prevDay.setHours(0, 0, 0, 0);
					if (prevDay < today) return;
				} else {
					newDate.setHours(currentHour - 1, 59, 0, 0); // drop hour, set mins to 59
					if (newDate < minDateTime) return; // respect min constraint
				}
				handleDateRollover(newDate); // ← single atomic update
			} else {
				const next = current - 1;
				handleTimeChange("minutes", next);
				setLocalMin(next.toString().padStart(2, "0"));
			}
		}
	};

	return (
		<div className='flex items-center justify-center gap-4 border-t border-gray-100 bg-gray-50/50 p-1'>
			<div className='flex items-center gap-3'>
				{/* Hours */}
				<div className='flex flex-col items-center gap-1'>
					<button
						type='button'
						onClick={() => increment("hours")}
						className='text-gray-400 hover:text-blue-600'
					>
						<ChevronUp className='h-4 w-4' />
					</button>
					<input
						type='text'
						className='w-10 rounded border border-gray-300 bg-white py-1 text-center text-sm outline-none focus:border-blue-500'
						value={localHour}
						onChange={(e) => handleInputChange("hours", e.target.value)}
						onBlur={() => handleBlur("hours")}
					/>
					<button
						type='button'
						onClick={() => decrement("hours")}
						disabled={isDecrementHourDisabled}
						className={cn(
							"transition-colors",
							isDecrementHourDisabled
								? "cursor-not-allowed text-gray-200"
								: "text-gray-400 hover:text-blue-600"
						)}
					>
						<ChevronDown className='h-4 w-4' />
					</button>
				</div>

				<span className='font-bold text-gray-400'>:</span>

				{/* Minutes */}
				<div className='flex flex-col items-center gap-1'>
					<button
						type='button'
						onClick={() => increment("minutes")}
						className='text-gray-400 hover:text-blue-600'
					>
						<ChevronUp className='h-4 w-4' />
					</button>
					<input
						type='text'
						className='w-10 rounded border border-gray-300 bg-white py-1 text-center text-sm outline-none focus:border-blue-500'
						value={localMin}
						onChange={(e) => handleInputChange("minutes", e.target.value)}
						onBlur={() => handleBlur("minutes")}
					/>
					<button
						type='button'
						onClick={() => decrement("minutes")}
						disabled={isDecrementMinDisabled}
						className={cn(
							"transition-colors",
							isDecrementMinDisabled
								? "cursor-not-allowed text-gray-200"
								: "text-gray-400 hover:text-blue-600"
						)}
					>
						<ChevronDown className='h-4 w-4' />
					</button>
				</div>
			</div>
		</div>
	);
};

// ─── FloatingDatePicker ────────────────────────────────────────────────────────
const FloatingDatePicker = ({
	value,
	onChange,
	label,
	error,
	disabled,
	tabIndex,
}: FloatingDateTimePickerProps) => {
	const [open, setOpen] = React.useState(false);
	const [mobileOpen, setMobileOpen] = React.useState(false);
	// In FloatingDatePicker, compute minTime
	const minDateTime = React.useMemo(() => addHours(new Date(), 2), []);

	// ── Temp state: changes are staged here until the user clicks "Set" ─────────
	const [tempDate, setTempDate] = React.useState<Date | undefined>(
		value ? new Date(value) : addHours(new Date(), 2)
	);

	// Keep tempDate in sync when value changes externally
	React.useEffect(() => {
		setTempDate(value ? new Date(value) : addHours(new Date(), 2));
	}, [value]);

	const nativeInputRef = React.useRef<HTMLInputElement>(null);

	const dateValue = value ? new Date(value) : undefined;
	const isFloating = !!dateValue || !!tempDate || open || mobileOpen;

	// Called when popover opens – snapshot current value into temp
	const handleOpen = (isMobile: boolean) => {
		if (disabled) return;

		setTempDate(value ? new Date(value) : addHours(new Date(), 2));
		isMobile ? setMobileOpen(true) : setOpen(true);
	};

	const handleCancel = () => {
		// Discard staged changes
		setTempDate(value ? new Date(value) : undefined);
		setOpen(false);
		setMobileOpen(false);
	};

	const handleSet = () => {
		// Commit staged changes
		if (tempDate) {
			onChange(format(tempDate, "yyyy-MM-dd'T'HH:mm:ss"));
			// onChange(tempDate.toISOString());
		}
		setOpen(false);
		setMobileOpen(false);
	};

	const handleFocus = () => {
		if (disabled) return;
		setTempDate(value ? new Date(value) : addHours(new Date(), 2));
		if (window.innerWidth < 768) {
			setMobileOpen(true);
		} else {
			setOpen(true);
		}
	};

	// ── Internal handlers write to tempDate, NOT directly to onChange ──────────
	const handleDateSelect = (newDate: Date | undefined) => {
		if (!newDate) return;
		const base = tempDate || new Date();

		const isSelectingToday =
			newDate.toDateString() === new Date().toDateString();
		const isSelectingMinDay =
			newDate.toDateString() === minDateTime.toDateString();

		let updated = setHours(
			setMinutes(newDate, base.getMinutes()),
			base.getHours()
		);

		// ✅ If selecting today or minDay, always enforce minDateTime
		if (isSelectingToday || isSelectingMinDay) {
			const currentTime = updated.getTime();
			const minTime = minDateTime.getTime();

			if (currentTime < minTime) {
				// ✅ Snap to minDateTime time
				updated = setHours(
					setMinutes(newDate, minDateTime.getMinutes()),
					minDateTime.getHours()
				);
			}
		}

		setTempDate(updated);
	};

	const handleTimeChange = (type: "hours" | "minutes", val: number) => {
		const base = tempDate || new Date();
		let updatedDate = new Date(base);

		if (type === "hours") {
			updatedDate = setHours(base, val); // ✅ val is already 0-23
		} else if (type === "minutes") {
			updatedDate = setMinutes(base, val);
		}

		setTempDate(updatedDate);
	};

	const handleDateRollover = (newDate: Date) => {
		setTempDate(newDate);
	};

	// ── Shared Set / Cancel footer ─────────────────────────────────────────────
	const ActionButtons = () => (
		<div className='flex items-center justify-end gap-2 border-t border-gray-100 bg-white px-3 py-2'>
			<button
				type='button'
				onClick={handleCancel}
				className='rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-50'
			>
				Cancel
			</button>
			<button
				type='button'
				onClick={handleSet}
				className='rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-blue-700'
			>
				Set
			</button>
		</div>
	);

	return (
		<div className='relative w-full'>
			<label
				className={cn(
					"pointer-events-none absolute left-3 z-10 bg-white px-1 transition-all duration-200",
					isFloating
						? "top-0 -translate-y-1/2 text-xs text-blue-600"
						: "top-1/2 -translate-y-1/2 text-sm text-gray-500",
					error && "text-red-500"
				)}
			>
				{label}
			</label>
			<div className='group top-0.2 absolute right-8 z-20 -translate-y-1/2'>
				<button
					type='button'
					className='bg-pointer-events-auto flex items-center gap-1 rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-xs font-medium whitespace-nowrap text-red-400 hover:bg-red-50'
				>
					<Info className='h-3 w-3' />
					Check operating hours
				</button>

				{/* Tooltip */}
				<div className='pointer-events-none absolute right-0 bottom-full z-50 mb-2 w-72 rounded-lg bg-gray-900 px-3 py-2 text-xs text-white opacity-0 shadow-lg transition-opacity duration-200 group-hover:pointer-events-auto group-hover:opacity-100'>
					<p className='leading-relaxed text-gray-200'>
						ARC Service Centers, operate Monday thru Friday, excluding some
						National Holidays. If you have a special production request, outside
						our normal business hours, please reach out to our ARC Customer Care
						team &nbsp;
						<a
							href='tel:8332728880'
							className='font-medium text-blue-400 underline hover:text-blue-300'
						>
							(833) 272-8880
						</a>
						&nbsp;or speak with your local ARC Service Center&nbsp;
						<a
							href='https://www.e-arc.com/location'
							target='_blank'
							rel='noreferrer'
							className='font-medium text-blue-400 underline hover:text-blue-300'
						>
							www.e-arc.com/location
						</a>
						.
					</p>
					<div className='absolute right-4 -bottom-1 h-2 w-2 rotate-45 bg-gray-900' />
				</div>
			</div>
			<input
				ref={nativeInputRef}
				tabIndex={tabIndex}
				type='text'
				disabled={disabled}
				className='absolute inset-0 z-10 h-full w-full cursor-pointer bg-transparent opacity-0'
				onFocus={handleFocus}
				readOnly
			/>

			<Popover
				open={open}
				onOpenChange={(next) => {
					if (!disabled) if (!next) handleCancel();
				}}
			>
				<PopoverTrigger asChild>
					<button
						type='button'
						tabIndex={-1}
						disabled={disabled}
						className={cn(
							"h-12 w-full rounded-lg border bg-white px-4 text-left text-sm transition-all outline-none",
							dateValue ? "text-gray-800" : "text-transparent",
							error ? "border-red-500" : "border-gray-300",
							disabled &&
								"cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500",
							open && "border-blue-500 ring-2 ring-blue-500/20"
						)}
					>
						{dateValue ? format(dateValue, "dd MMM yyyy, HH:mm") : "\u00A0"}
						<CalendarIcon className='pointer-events-none absolute top-3.5 right-3 h-5 w-5 text-gray-400' />
					</button>
				</PopoverTrigger>

				<PopoverContent
					className='w-auto rounded-lg p-0 shadow-xl'
					align='start'
					side='top'
					avoidCollisions={false}
				>
					<div className='overflow-hidden' style={{ height: "255px" }}>
						<div className='origin-top scale-[0.85] pt-2'>
							{" "}
							<Calendar
								mode='single'
								selected={tempDate}
								onSelect={handleDateSelect}
								disabled={{ before: new Date() }}
							/>
						</div>
					</div>
					<TimePicker
						dateValue={tempDate}
						handleTimeChange={handleTimeChange}
						minDateTime={minDateTime}
						handleDateRollover={handleDateRollover}
					/>
					<ActionButtons />
				</PopoverContent>
			</Popover>

			{/* Mobile Sheet */}
			{/* Mobile Sheet */}
			{mobileOpen && (
				<div className='fixed inset-0 z-50 flex items-end md:hidden'>
					<div
						className='absolute inset-0 bg-black/40'
						onClick={handleCancel}
					/>
					<div className='relative w-full rounded-t-2xl bg-white px-4 pt-4 pb-8 shadow-xl'>
						<div className='mx-auto mb-4 h-1 w-10 rounded-full bg-gray-300' />
						<div className='mb-3 flex items-center justify-between'>
							<span className='text-sm font-semibold'>{label}</span>
							<button onClick={handleCancel}>
								<X className='h-5 w-5' />
							</button>
						</div>
						<div className='overflow-hidden' style={{ height: "255px" }}>
							<div className='origin-top scale-[0.85] pt-2'>
								<Calendar
									mode='single'
									selected={tempDate}
									onSelect={handleDateSelect}
									disabled={{ before: new Date() }}
								/>
							</div>
						</div>
						<TimePicker
							dateValue={tempDate}
							handleTimeChange={handleTimeChange}
							minDateTime={minDateTime}
							handleDateRollover={handleDateRollover} //make sure this is here
						/>
						<ActionButtons />
					</div>
				</div>
			)}

			{error && <p className='mt-1 text-xs text-red-600'>{error}</p>}
		</div>
	);
};

export { FloatingDatePicker };
